import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  HandleFinishParams,
  KEY_OPPORTUNITIES_HIGHLIGHTED_LIST,
  OpportunitiesItem,
  OpportunityType,
  useGetOpportunitiesHighlightedListInfiniteQuery,
  useSetFinishedOpportunityActiveBaseMutation,
  useSetFinishedOpportunityOdcMutation,
  useSetOpportunityActiveBaseUnHighlightedMutation,
  useSetOpportunityOdcUnHighlightedMutation,
  useSetUnFinishedOpportunityActiveBaseMutation,
  useSetUnFinishedOpportunityOdcMutation
} from 'integration/resources/newOpportunities'
import { useBreakpointValue, useToast } from 'native-base'
import { Toast } from 'organisms'
import { useQueryClient } from 'react-query'
import { ListType } from 'src/screens/OrganizationScreen/OrganizationScreen.types'
import { calculateActiveFilterCount } from 'src/utils/filters'
import { LogEvent } from 'src/utils/logEvents'

import { UseHighlighted } from './types'

const mergeAllDataInfiniteQuery = (
  data: ReturnType<typeof useGetOpportunitiesHighlightedListInfiniteQuery>['data']
): OpportunitiesItem[] => {
  return (
    data?.pages?.reduce<OpportunitiesItem[]>((previousValue, currentValue) => {
      return [...previousValue, ...(currentValue.data.data || [])]
    }, []) ?? []
  )
}

export const useHighlighted: UseHighlighted = ({
  route,
  search,
  navigation,
  openOpportunityFilterModal
}) => {
  const allFilters = useMemo(() => {
    return {
      segment: route?.params?.segment,
      network: route?.params?.network,
      subsidiary: route?.params?.subsidiary,
      opportunity_type: route?.params?.opportunity_type,
      user_std_code: route?.params?.user_std_code
    }
  }, [route?.params])

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch: refetchHook
  } = useGetOpportunitiesHighlightedListInfiniteQuery(
    {
      per_page: 10,
      search: search ?? undefined,
      segment: allFilters.segment,
      network: allFilters.network,
      subsidiary: allFilters.subsidiary,
      opportunity_type: allFilters.opportunity_type,
      user_std_code: allFilters.user_std_code
    },
    undefined
  )

  const list = useMemo(() => mergeAllDataInfiniteQuery(data), [data])

  const totalItems = data?.pages[0].data.metadata.pagination.total ?? 0

  const fetchMore = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage()
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage])

  const [refetchLoading, setRefetchLoading] = useState(false)

  const queryClient = useQueryClient()

  const refetch = useCallback(() => {
    setRefetchLoading(true)

    setTimeout(() => {
      setRefetchLoading(false)
    }, 300)

    queryClient.invalidateQueries(KEY_OPPORTUNITIES_HIGHLIGHTED_LIST)
  }, [queryClient])

  const isEmpty = useMemo(() => !list?.length, [list])

  const handleGoToDetails = (opportunityId: string, itemType?: OpportunityType) => {
    LogEvent(`oportunidades_destacadas_acessar_detalhes`, {
      item_id: opportunityId
    })

    navigation.navigate('OpportunityDetails', {
      id: opportunityId,
      type: itemType
    })
  }

  const isMobile = useBreakpointValue({ base: true, lg: false })

  const toast = useToast()

  const { mutateAsync: setOpportunityOdcUnHighlighted } =
    useSetOpportunityOdcUnHighlightedMutation()

  const { mutateAsync: setOpportunityActiveBaseUnHighlighted } =
    useSetOpportunityActiveBaseUnHighlightedMutation()

  const [opportunityHighlightIsLoading, setOpportunityHighlightIsLoading] = useState('')

  const handleHighlight = (
    id: string,
    highlighted: boolean,
    opportunity_type_enum: OpportunityType
  ) => {
    setOpportunityHighlightIsLoading(id)

    LogEvent(`oportunidades_destacadas_${highlighted ? 'desfazer_' : ''}destacar`, {
      item_id: id
    })

    const isActiveBase = opportunity_type_enum === OpportunityType.ACTIVE_BASE

    const unHighlightedMutate = isActiveBase
      ? setOpportunityActiveBaseUnHighlighted
      : setOpportunityOdcUnHighlighted

    return (
      id &&
      unHighlightedMutate(
        {
          id
        },
        {
          onError: () => {
            toast.show({
              render: () => <Toast type="error" text="Tente novamente mais tarde" />,
              duration: 3000
            })

            setOpportunityHighlightIsLoading('')
          },
          onSuccess: () => {
            refetch()

            setOpportunityHighlightIsLoading('')
          }
        }
      )
    )
  }

  const [opportunityConcludeIsLoading, setOpportunityConcludeIsLoading] = useState('')

  const { mutateAsync: setOpportunityOdcFinished } = useSetFinishedOpportunityOdcMutation()

  const { mutateAsync: setOpportunityActiveBaseFinished } =
    useSetFinishedOpportunityActiveBaseMutation()

  const { mutateAsync: setOpportunityOdcUnFinished } = useSetUnFinishedOpportunityOdcMutation()

  const { mutateAsync: setOpportunityActiveBaseUnFinished } =
    useSetUnFinishedOpportunityActiveBaseMutation()

  const handleConclude = (data: HandleFinishParams) => {
    const { finished, id, comment, insight, only_finish, score, opportunity_type_enum } = data

    LogEvent(`oportunidades_destacadas_${finished ? 'desfazer_' : ''}concluir`, {
      item_id: id
    })

    setOpportunityConcludeIsLoading(id)

    const isActiveBase = opportunity_type_enum === OpportunityType.ACTIVE_BASE

    return (
      id &&
      (finished
        ? isActiveBase
          ? setOpportunityActiveBaseUnFinished
          : setOpportunityOdcUnFinished
        : isActiveBase
        ? setOpportunityActiveBaseFinished
        : setOpportunityOdcFinished)(
        {
          id,
          only_finish,
          comment,
          insight,
          score
        },
        {
          onError: () => {
            toast.show({
              render: () => <Toast type="error" text="Tente novamente mais tarde" />,
              duration: 3000
            })

            setOpportunityConcludeIsLoading('')
          },
          onSuccess: () => {
            refetch()

            setOpportunityConcludeIsLoading('')
          }
        }
      )
    )
  }

  const activeFilterCount = calculateActiveFilterCount(allFilters)

  const handleGoToFilters = () => {
    LogEvent(`oportunidades_destacadas_acessar_filtros`, {
      item_id: ''
    })

    if (isMobile) {
      navigation.navigate('OpportunityFilter', {
        listType: ListType.Highlighted,
        offer: route?.params?.offer,
        public_rating: route?.params?.public_rating,
        segment: route?.params?.segment,
        network: route?.params?.network,
        subsidiary: route?.params?.subsidiary,
        opportunity_type: route?.params?.opportunity_type,
        user_std_code: route?.params?.user_std_code,
        order_by: route?.params?.order_by,
        order_by_direction: route?.params?.order_by_direction
      })

      return
    }

    openOpportunityFilterModal?.(ListType.Highlighted)
  }

  useEffect(() => {
    refetchHook()
  }, [search, refetchHook])

  return {
    opportunitiesList: list,
    isLoading,
    fetchMore,
    isFetchingNextPage,
    refetch,
    refreshing: refetchLoading,
    isEmpty,
    handleGoToDetails,
    isMobile,
    handleHighlight,
    highlightIsLoading: opportunityHighlightIsLoading,
    handleConclude,
    concludeIsLoading: opportunityConcludeIsLoading,
    totalItems,
    handleGoToFilters,
    activeFilterCount
  }
}
